/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';

import { Typography2 } from '@coursera/cds-core';
import { DegreeIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { getUpsellCopy } from 'bundles/stackability-common/stackability-common-index';
import DegreesModalCta from 'bundles/unified-description-page-common/components/modules/credits/DegreesModalCta';
import LearnMoreLink from 'bundles/unified-description-page-common/components/shared/LearnMoreLink';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { CREDITS_ANCHOR_ID } from 'bundles/unified-description-page-common/constants';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type {
  DegreeFragment,
  PartnerFragment,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import { formatPageDataDegrees } from 'bundles/unified-description-page-common/utils/formatPageDataDegrees';
import getEligibleDegreesDescription from 'bundles/unified-description-page-common/utils/getEligibleDegreesDescription';
import getEligibleDegreesTitle from 'bundles/unified-description-page-common/utils/getEligibleDegreesTitle';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';

import ACELogo from 'images/unified-description-page/pathways-ace-logo.png';

import _t from 'i18n!nls/xdp';

type Props = {
  degrees?: DegreeFragment[] | null;
  partners?: PartnerFragment[] | null;
  slug?: string;
  productTags?: ConsumerSpecializationPageFieldsFragment['productTags'];
  productVariant?: ConsumerSpecializationPageFieldsFragment['productVariant'];
  disableCta?: boolean;
  disableAceCta?: boolean;
};

const styles = {
  item: css`
    display: flex;
    gap: var(--cds-spacing-200);
  `,
  iconContainer: css`
    border-radius: 8px;
    background: var(--cds-color-neutral-background-primary-weak);
    display: flex;
    width: 76px;
    height: 54px;
    padding: calc(var(--cds-spacing-100) / 2);
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  `,
  title: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  titleRedesign: css`
    margin-bottom: var(--cds-spacing-50);
  `,
  description: css`
    max-width: 608px;
  `,
  aceSection: css`
    display: flex;
    padding-top: var(--cds-spacing-150);
    gap: var(--cds-spacing-100);
  `,
};

const getProductVariant = (pageData: {
  primarySpecialization?: ConsumerCoursePageFieldsFragment['primarySpecialization'];
  productVariant?: ConsumerSpecializationPageFieldsFragment['productVariant'];
}) => {
  if (pageData.productVariant) {
    return pageData.productVariant;
  } else if (pageData.primarySpecialization?.productVariant) {
    return pageData.primarySpecialization.productVariant;
  }
  return undefined;
};

const EligibleDegreesItem = ({
  degrees,
  partners,
  slug,
  productTags,
  productVariant,
  disableCta,
  disableAceCta,
}: Props) => {
  const hasDegrees = degrees && degrees.length > 0;
  const hasACELink = !!productTags?.linkToACECreditRecommendation;
  const eventingProductData = useEventingProductData();
  const track = useTracker();
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;

  if (!hasDegrees && !hasACELink) {
    return null;
  }

  const eligibleDegreesTitle = hasDegrees
    ? getEligibleDegreesTitle(degrees ?? [])
    : _t('Get a head start on your degree');

  const variantTitle = getVariantTitle(productVariant);
  const upsellCopy = getUpsellCopy().homepagesNavXdp;

  const { description: eligibleDegreesDescription, disclaimer: eligibleDegreesDisclaimer } =
    getEligibleDegreesDescription({
      productName: variantTitle,
      specializationType: getProductVariant({
        primarySpecialization: undefined,
        productVariant,
      }),
      degreePartnerName: degrees?.[0]?.upsellPartners?.[0]?.name,
      upsellType: degrees?.[0]?.upsellType,
      productPagePartnerName: partners?.[0]?.name,
      disableCtaCopy: disableCta,
    });

  return (
    <Section anchorId={CREDITS_ANCHOR_ID} spacing={0}>
      <div css={styles.item}>
        <div css={styles.iconContainer}>
          <DegreeIcon size="large" color="interactive" width={34} height={34} />
        </div>
        <div>
          <Typography2
            component="h3"
            variant={enableXDPRedesign ? 'subtitleMedium' : 'subtitleLarge'}
            css={enableXDPRedesign ? styles.titleRedesign : styles.title}
          >
            {eligibleDegreesTitle}
          </Typography2>
          <Typography2 component="p" variant="bodySecondary" css={styles.description}>
            {eligibleDegreesDescription}
          </Typography2>
          &nbsp;
          <DegreesModalCta
            modalDescription={<Typography2 component="p">{eligibleDegreesDescription}</Typography2>}
            descriptionDisclaimer={eligibleDegreesDisclaimer}
            disableCta={disableCta}
            modalTitle={eligibleDegreesTitle}
            degrees={formatPageDataDegrees(degrees ?? [])}
            inlineCta
          />
          {hasACELink && (
            <div css={styles.aceSection}>
              <Imgix src={ACELogo} alt={_t('ACE Logo')} maxWidth={31} maxHeight={31} height={31} width={31} />
              <Typography2 component="p" variant="bodySecondary">
                <span>{upsellCopy.variantAceCreditRecommended(variantTitle)}</span>
                &nbsp;
                {!disableAceCta && productTags?.linkToACECreditRecommendation && (
                  <LearnMoreLink
                    href={productTags?.linkToACECreditRecommendation}
                    trackingName="ace_credit_learn_more"
                    ariaLabel={_t('Learn more about ACE Recommendation')}
                    target="_blank"
                    onClick={() => {
                      track('click_button', {
                        button: {
                          name: 'description_page_ace',
                        },
                        pageSection: {
                          sectionName: 'eligible_degree',
                        },
                        ...eventingProductData,
                      });
                    }}
                  />
                )}
              </Typography2>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default EligibleDegreesItem;
